import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Check, Search, Save, AlertCircle, Users, Shield, Eye, EyeOff, RefreshCw, Key } from 'lucide-react';
import { APP_MODULES, MODULE_GROUPS, ACTION_LABELS, actionPerm, REPORT_HEADS, REPORT_SECTIONS, reportPerm } from './permissions';

// ═══════════════════════════════════════════════════════════════════════
// API CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════
// Must match the rest of the app (login / CRM) so roles & users are managed on
// the same backend that authentication validates against.
const API_BASE_URL = 'https://crm.securewheels.pk/api';

const api = {
  async call(endpoint, method = 'GET', data = null) {
    const options = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (data && method !== 'GET') {
      options.body = JSON.stringify(data);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, options);
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'API request failed');
      return result;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  },
  roles: {
    getAll: () => api.call('roles.php'),
    create: (data) => api.call('roles.php', 'POST', data),
    update: (data) => api.call('roles.php', 'PUT', data),
    delete: (id) => api.call('roles.php', 'DELETE', { id }),
  },
  employees: {
    getAll: () => api.call('employees.php'),
  },
  users: {
    getAll: () => api.call('auth.php', 'POST', { action: 'get_users' }),
    create: (data) => api.call('auth.php', 'POST', { ...data, action: 'create_user' }),
    update: (data) => api.call('auth.php', 'POST', { ...data, action: 'update_user' }),
    delete: (id, deleteType = 'deactivate') => api.call('auth.php', 'POST', {
      action: 'delete_user',
      id,
      delete_type: deleteType,
    }),
    resetPassword: (userId, newPassword) => api.call('auth.php', 'POST', {
      action: 'reset_password',
      user_id: userId,
      new_password: newPassword,
    }),
  },
};

// ═══════════════════════════════════════════════════════════════════════
// PERMISSION STRUCTURE
// ═══════════════════════════════════════════════════════════════════════
// Module-level access. Each app module = one permission the role can grant.
// Sourced from the shared registry (permissions.js) so the roles UI and the
// CRM enforcement can never drift apart.

const LEVEL_STYLES = {
  super:  { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Super' },
  high:   { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
  medium: { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Medium' },
  low:    { bg: 'bg-slate-100',  text: 'text-slate-600',  label: 'Low' },
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
export default function UserManagement() {
  const [activeTab, setActiveTab] = useState('roles');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  // confirmDialog shape: { title, message, buttons: [{ label, value, style }], resolve }

  const showConfirm = (title, message, buttons) =>
    new Promise((resolve) => setConfirmDialog({ title, message, buttons, resolve }));

  const handleConfirmChoice = (value) => {
    if (confirmDialog?.resolve) confirmDialog.resolve(value);
    setConfirmDialog(null);
  };

  const notify = (type, msg) => {
    setNotification({ type, msg });
    setTimeout(() => setNotification(null), 3500);
  };

  // ─────────────────────────────────────────────────────────────────────
  // ROLES STATE
  // ─────────────────────────────────────────────────────────────────────
  const [roles, setRoles] = useState([]);
  const [roleView, setRoleView] = useState('list');
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleSearch, setRoleSearch] = useState('');
  const emptyRoleForm = {
    name: '', description: '', level: 'medium',
    crm_access: 1, mobile_access: 'none', nr_access: 0, pipeline_access: 0, permissions: [],
  };
  const [roleFormData, setRoleFormData] = useState(emptyRoleForm);

  // ─────────────────────────────────────────────────────────────────────
  // USERS STATE
  // ─────────────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [userView, setUserView] = useState('list');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [empSearch, setEmpSearch] = useState('');      // employee-link searchable dropdown query
  const [empDropOpen, setEmpDropOpen] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({ userId: null, newPassword: '', confirmPassword: '' });
  const emptyUserForm = {
    username: '', full_name: '', employee_code: '',
    email: '', password: '', confirmPassword: '',
    role_id: '', status: 'active',
    // SW Crew app modules — ticked per user (no longer part of the role)
    access_sales: false, access_technician: false, access_nr: false, access_pipeline: false,
  };
  const [userFormData, setUserFormData] = useState(emptyUserForm);
  const CREW_MODULES = [   // SW Crew app modules (NOT the CRM APP_MODULES from permissions.js)
  ['access_sales',      'Sales module',      'Open Sales, post a sale, payments'],
  ['access_technician', 'Technician module', 'Tasks, open jobs, stock'],
  ['access_nr',         'NR Vehicles',       'Not-reporting follow-ups & REDO'],
  ['access_pipeline',   'Sales Pipeline',    'Call leads, mature / follow-up / cancel'],
];

  // ═════════════════════════════════════════════════════════════════════
  // LOAD DATA
  // ═════════════════════════════════════════════════════════════════════
  const loadRoles = async () => {
    try {
      const result = await api.roles.getAll();
      if (result.data) setRoles(result.data);
    } catch (error) {
      notify('error', 'Failed to load roles: ' + error.message);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await api.users.getAll();
      if (result.data) setUsers(result.data);
    } catch (error) {
      notify('error', 'Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const result = await api.employees.getAll();
      if (result.data) setEmployees(result.data);
    } catch (error) {
      // non-fatal: the user form can still be filled by hand
      console.warn('Failed to load employees for linking:', error.message);
    }
  };

  // Load roles on mount — needed for both tabs (role dropdown in user form)
  useEffect(() => { loadRoles(); }, []);
  useEffect(() => { if (activeTab === 'accounts') { loadUsers(); loadEmployees(); } }, [activeTab]);

  // ═════════════════════════════════════════════════════════════════════
  // ROLE HANDLERS
  // ═════════════════════════════════════════════════════════════════════
  const handleCreateRole = () => {
    setSelectedRole(null);
    setRoleFormData(emptyRoleForm);
    setRoleView('form');
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    const permissionsArray = role.permissions ? JSON.parse(role.permissions) : [];
    setRoleFormData({
      name: role.name,
      description: role.description || '',
      level: role.level || 'medium',
      crm_access: Number(role.crm_access) || 0,
      mobile_access: role.mobile_access || 'none',
      nr_access: Number(role.nr_access) || 0,
      pipeline_access: Number(role.pipeline_access) || 0,
      permissions: permissionsArray,
    });
    setRoleView('form');
  };

  const handleSaveRole = async () => {
    if (!roleFormData.name.trim()) {
      notify('error', 'Role name is required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...roleFormData,
        permissions: JSON.stringify(roleFormData.permissions),
      };
      if (selectedRole) {
        await api.roles.update({ id: selectedRole.id, ...payload });
        notify('success', 'Role updated successfully');
      } else {
        await api.roles.create(payload);
        notify('success', 'Role created successfully');
      }
      loadRoles();
      setRoleView('list');
    } catch (error) {
      notify('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (id) => {
    const choice = await showConfirm(
      'Delete Role',
      'Delete this role? Users assigned to this role will lose access.',
      [
        { label: 'Cancel', value: false, style: 'secondary' },
        { label: 'Delete', value: true, style: 'danger' },
      ]
    );
    if (!choice) return;
    setLoading(true);
    try {
      await api.roles.delete(id);
      notify('success', 'Role deleted successfully');
      loadRoles();
    } catch (error) {
      notify('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permissionId) => {
    setRoleFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  // All extra perm ids owned by a module (actions, and report sections for Reports).
  const subPermsOf = (mod) => {
    const ids = (mod.actions || []).map(a => actionPerm(mod.key, a));
    if (mod.key === 'reports') ids.push(...REPORT_SECTIONS.map(s => reportPerm(s.key)));
    return ids;
  };

  // Toggle a module's view/open permission. Turning it OFF also clears that
  // module's action + report-section perms (meaningless without view access).
  const toggleModule = (mod) => {
    setRoleFormData(prev => {
      const has = prev.permissions.includes(mod.perm);
      if (has) {
        const subIds = subPermsOf(mod);
        return {
          ...prev,
          permissions: prev.permissions.filter(p => p !== mod.perm && !subIds.includes(p)),
        };
      }
      return { ...prev, permissions: [...prev.permissions, mod.perm] };
    });
  };

  // Select / clear every module (+ its actions/report sections) in a group at once.
  const setGroupPermissions = (groupKey, on) => {
    const groupMods = APP_MODULES.filter(m => m.group === groupKey);
    const groupPerms = groupMods.flatMap(m => [m.perm, ...subPermsOf(m)]);
    setRoleFormData(prev => {
      const others = prev.permissions.filter(p => !groupPerms.includes(p));
      return { ...prev, permissions: on ? [...others, ...groupPerms] : others };
    });
  };

  // ═════════════════════════════════════════════════════════════════════
  // USER HANDLERS
  // ═════════════════════════════════════════════════════════════════════
  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserFormData(emptyUserForm);
    setEmpSearch(''); setEmpDropOpen(false);
    setUserView('form');
  };

  // Pick an existing employee → auto-fill the account fields (so the office doesn't
  // re-type everything). The employee_code is what links the login to the employee
  // (tasks, sales & technician dashboards all resolve the employee via this code).
  // Username suggestion = the employee code in lowercase (e.g. EMP-001 → emp-001).
  const suggestUsername = (code) => (code || '').toLowerCase().trim();

  const applyEmployee = (empId) => {
    if (!empId) { setUserFormData(p => ({ ...p, employee_code: '' })); return; }
    const emp = employees.find(e => String(e.id) === String(empId));
    if (!emp) return;
    setUserFormData(p => ({
      ...p,
      full_name: emp.name || p.full_name,
      employee_code: emp.employee_code || '',
      email: emp.email || p.email,
      // only auto-suggest a username when the field is still empty (don't clobber edits)
      username: p.username.trim() ? p.username : suggestUsername(emp.employee_code),
    }));
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserFormData({
      username: user.username,
      full_name: user.full_name || '',
      employee_code: user.employee_code || '',
      email: user.email || '',
      password: '',
      confirmPassword: '',
      role_id: String(user.role_id || ''),
      status: user.status || 'active',
      access_sales: Number(user.access_sales) === 1,
      access_technician: Number(user.access_technician) === 1,
      access_nr: Number(user.access_nr) === 1,
      access_pipeline: Number(user.access_pipeline) === 1,
    });
    setEmpSearch(''); setEmpDropOpen(false);
    setUserView('form');
  };

  const handleSaveUser = async () => {
    if (!userFormData.username.trim() || !userFormData.full_name.trim()) {
      notify('error', 'Username and Full Name are required');
      return;
    }

    if (!selectedUser) {
      if (!userFormData.password || userFormData.password.length < 6) {
        notify('error', 'Password must be at least 6 characters');
        return;
      }
      if (userFormData.password !== userFormData.confirmPassword) {
        notify('error', 'Passwords do not match');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        username: userFormData.username,
        full_name: userFormData.full_name,
        employee_code: userFormData.employee_code || null,
        email: userFormData.email || null,
        role_id: userFormData.role_id ? Number(userFormData.role_id) : null,
        status: userFormData.status,
        access_sales: userFormData.access_sales ? 1 : 0,
        access_technician: userFormData.access_technician ? 1 : 0,
        access_nr: userFormData.access_nr ? 1 : 0,
        access_pipeline: userFormData.access_pipeline ? 1 : 0,
      };

      if (selectedUser) {
        await api.users.update({ id: selectedUser.id, ...payload });
        notify('success', 'User updated successfully');
      } else {
        await api.users.create({ ...payload, password: userFormData.password });
        notify('success', 'User created successfully');
      }

      loadUsers();
      setUserView('list');
    } catch (error) {
      notify('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const choice = await showConfirm(
      'Delete User',
      'What would you like to do with this user?',
      [
        { label: 'Cancel', value: null, style: 'secondary' },
        { label: 'Deactivate', value: 'deactivate', style: 'warning' },
        { label: 'Delete Permanently', value: 'permanent', style: 'danger' },
      ]
    );
    if (!choice) return;
    const deleteType = choice;

    setLoading(true);
    try {
      await api.users.delete(userId, deleteType);
      notify('success', deleteType === 'permanent' ? 'User deleted permanently' : 'User deactivated');
      loadUsers();
    } catch (error) {
      notify('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenResetPassword = (userId) => {
    setResetPasswordData({ userId, newPassword: '', confirmPassword: '' });
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordData.newPassword || resetPasswordData.newPassword.length < 6) {
      notify('error', 'Password must be at least 6 characters');
      return;
    }
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      notify('error', 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.users.resetPassword(resetPasswordData.userId, resetPasswordData.newPassword);
      notify('success', 'Password reset successfully');
      setShowResetPasswordModal(false);
      setResetPasswordData({ userId: null, newPassword: '', confirmPassword: '' });
    } catch (error) {
      notify('error', 'Failed to reset password: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // FILTERED DATA
  // ─────────────────────────────────────────────────────────────────────
  const filteredRoles = roles.filter(r =>
    r.name.toLowerCase().includes(roleSearch.toLowerCase()) ||
    (r.description && r.description.toLowerCase().includes(roleSearch.toLowerCase()))
  );

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.full_name && u.full_name.toLowerCase().includes(userSearch.toLowerCase())) ||
    (u.email && u.email.toLowerCase().includes(userSearch.toLowerCase()))
  );

  // ═════════════════════════════════════════════════════════════════════
  // RENDER: ROLES LIST
  // ═════════════════════════════════════════════════════════════════════
  const renderRolesList = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search roles..."
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:border-blue-400 outline-none text-sm"
          />
        </div>
        <button
          onClick={handleCreateRole}
          className="ml-4 px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 flex items-center gap-2 transition-colors"
        >
          <Plus size={18} /> Create Role
        </button>
      </div>

      <div className="grid gap-4">
        {filteredRoles.map(role => {
          const style = LEVEL_STYLES[role.level] || LEVEL_STYLES.medium;
          const permCount = role.permissions ? JSON.parse(role.permissions).length : 0;
          return (
            <div key={role.id} className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-slate-900">{role.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${style.bg} ${style.text}`}>
                      {style.label} Access
                    </span>
                  </div>
                  {role.description && <p className="text-sm text-slate-600 mb-3">{role.description}</p>}
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Shield size={14} />
                      {permCount} permissions
                    </span>
                    {role.crm_access === 1 && <span className="text-green-600 font-semibold">✓ CRM Access</span>}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEditRole(role)}
                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                    title="Edit Role"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteRole(role.id)}
                    className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                    title="Delete Role"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {filteredRoles.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Shield size={48} className="mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No roles found</p>
          </div>
        )}
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════
  // RENDER: ROLE FORM
  // ═════════════════════════════════════════════════════════════════════
  const renderRoleForm = () => (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900">
          {selectedRole ? 'Edit Role' : 'Create New Role'}
        </h2>
        <button onClick={() => setRoleView('list')} className="p-2 hover:bg-slate-100 rounded-lg">
          <X size={20} />
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-5">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Role Name *</label>
          <input
            type="text"
            value={roleFormData.name}
            onChange={(e) => setRoleFormData(p => ({ ...p, name: e.target.value }))}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-blue-400 outline-none text-sm"
            placeholder="e.g., Workshop Manager"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
          <textarea
            value={roleFormData.description}
            onChange={(e) => setRoleFormData(p => ({ ...p, description: e.target.value }))}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-blue-400 outline-none text-sm"
            rows="3"
            placeholder="Brief description of this role's responsibilities"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Access Level</label>
            <select
              value={roleFormData.level}
              onChange={(e) => setRoleFormData(p => ({ ...p, level: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-blue-400 outline-none text-sm"
            >
              {Object.entries(LEVEL_STYLES).map(([key, style]) => (
                <option key={key} value={key}>{style.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">CRM Access</label>
            <select
              value={roleFormData.crm_access}
              onChange={(e) => setRoleFormData(p => ({ ...p, crm_access: Number(e.target.value) }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-blue-400 outline-none text-sm"
            >
              <option value={0}>No Access</option>
              <option value={1}>Full Access</option>
            </select>
          </div>

        </div>

      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Shield size={20} /> Module Access
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Tick a module to let this role <b>open</b> it, then choose the actions
          (<b>Create / Edit / Delete</b>) it may perform there — a module with no actions ticked is
          view-only. A role with <b>no modules</b> ticked is treated as "not configured yet" and can
          still see everything — tick at least one to start enforcing.
        </p>
        <div className="space-y-6">
          {MODULE_GROUPS.map(group => {
            const groupModules = APP_MODULES.filter(m => m.group === group.key);
            const allOn = groupModules.every(m => roleFormData.permissions.includes(m.perm));
            return (
              <div key={group.key} className="border-b border-slate-100 pb-5 last:border-0 last:pb-0">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{group.icon}</span>
                    <div>
                      <h4 className="font-bold text-slate-800">{group.name}</h4>
                      <p className="text-xs text-slate-500">{group.desc}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGroupPermissions(group.key, !allOn)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    {allOn ? 'Clear all' : 'Select all'}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {groupModules.map(mod => {
                    const checked = roleFormData.permissions.includes(mod.perm);
                    return (
                      <div
                        key={mod.perm}
                        className={`p-3 rounded-lg border transition-colors ${
                          checked ? 'border-blue-300 bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleModule(mod)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-400"
                          />
                          <span className="font-semibold text-sm text-slate-800">{mod.label}</span>
                          {!mod.actions && <span className="text-[10px] text-slate-400 font-medium">view only</span>}
                        </label>
                        {checked && mod.actions && (
                          <div className="flex flex-wrap gap-2 mt-2 ml-7">
                            {mod.actions.map(action => {
                              const pid = actionPerm(mod.key, action);
                              const on = roleFormData.permissions.includes(pid);
                              return (
                                <button
                                  key={pid}
                                  type="button"
                                  onClick={() => togglePermission(pid)}
                                  className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                                    on
                                      ? (action === 'delete'
                                          ? 'bg-red-500 text-white border-red-500'
                                          : 'bg-blue-500 text-white border-blue-500')
                                      : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'
                                  }`}
                                >
                                  {on ? '✓ ' : ''}{ACTION_LABELS[action]}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {checked && mod.key === 'reports' && (
                          <div className="mt-2 ml-7 space-y-2.5">
                            <p className="text-[11px] text-slate-400 italic">Pick which reports this role can open (none picked = all reports).</p>
                            {REPORT_HEADS.map(head => (
                              <div key={head.key}>
                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">{head.label}</p>
                                <div className="flex flex-wrap gap-2">
                                  {REPORT_SECTIONS.filter(s => s.head === head.key).map(s => {
                                    const pid = reportPerm(s.key);
                                    const on = roleFormData.permissions.includes(pid);
                                    return (
                                      <button
                                        key={pid}
                                        type="button"
                                        onClick={() => togglePermission(pid)}
                                        className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                                          on
                                            ? 'bg-indigo-500 text-white border-indigo-500'
                                            : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'
                                        }`}
                                      >
                                        {on ? '✓ ' : ''}{s.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <button
          onClick={() => setRoleView('list')}
          className="px-6 py-2 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveRole}
          disabled={loading}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
        >
          <Save size={16} /> {loading ? 'Saving...' : 'Save Role'}
        </button>
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════
  // RENDER: USERS TAB
  // ═════════════════════════════════════════════════════════════════════
  const renderUsersTab = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
          />
        </div>
        <button
          onClick={handleCreateUser}
          className="ml-4 px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 flex items-center gap-2 transition-colors"
        >
          <Plus size={18} /> Create User
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="animate-spin mx-auto mb-3 text-slate-400" size={32} />
          <p className="text-slate-500">Loading users...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase">Employee Code</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map(user => {
                const userRole = roles.find(r => r.id === user.role_id);
                return (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-semibold text-slate-900">{user.full_name}</div>
                        <div className="text-xs text-slate-500">@{user.username}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {user.employee_code || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {user.email || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {userRole ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                          <Shield size={12} />
                          {userRole.name}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">No role</span>
                      )}
                      {/* SW Crew app modules ticked for this user */}
                      {CREW_MODULES.some(([k]) => Number(user[k]) === 1) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {CREW_MODULES.filter(([k]) => Number(user[k]) === 1).map(([k, title]) => (
                            <span key={k} className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-[10px] font-semibold">📱 {title.replace(' module', '')}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${
                        user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-1.5 hover:bg-blue-50 text-blue-600 rounded transition-colors"
                          title="Edit User"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleOpenResetPassword(user.id)}
                          className="p-1.5 hover:bg-orange-50 text-orange-600 rounded transition-colors"
                          title="Reset Password"
                        >
                          <Key size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-1.5 hover:bg-red-50 text-red-600 rounded transition-colors"
                          title="Delete User"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Users size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No users found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════
  // RENDER: USER FORM
  // ═════════════════════════════════════════════════════════════════════
  const renderUserForm = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900">
          {selectedUser ? 'Edit User' : 'Create New User'}
        </h2>
        <button onClick={() => setUserView('list')} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
        {/* Link to an existing employee → auto-fills the fields below (no re-typing).
            Searchable dropdown, active employees only. */}
        {(() => {
          const linkedEmp = employees.find(e => e.employee_code && e.employee_code === userFormData.employee_code);
          const linkedCodes = new Set(users.filter(u => u.id !== selectedUser?.id && u.employee_code).map(u => u.employee_code));
          const q = empSearch.trim().toLowerCase();
          const activeEmps = employees.filter(e => (e.status || 'active') === 'active');
          const matches = activeEmps.filter(e => {
            if (!q) return true;
            return [e.name, e.employee_code, e.designation, e.department]
              .some(f => (f || '').toLowerCase().includes(q));
          }).slice(0, 30);
          const pick = (emp) => { applyEmployee(String(emp.id)); setEmpSearch(''); setEmpDropOpen(false); };
          return (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="block text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                <Users size={15} /> Link to Employee
                <span className="text-xs font-normal text-blue-500">— auto-fills name, code & email</span>
              </label>

              {linkedEmp ? (
                <div className="flex items-center gap-2 bg-white border border-blue-300 rounded-lg px-3 py-2">
                  <Check size={15} className="text-green-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{linkedEmp.name}</p>
                    <p className="text-xs text-slate-500 truncate">{linkedEmp.employee_code}{linkedEmp.designation ? ` · ${linkedEmp.designation}` : ''}</p>
                  </div>
                  <button type="button" onClick={() => applyEmployee('')}
                    className="p-1 text-slate-400 hover:text-red-500" title="Unlink employee">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    value={empSearch}
                    onChange={(e) => { setEmpSearch(e.target.value); setEmpDropOpen(true); }}
                    onFocus={() => setEmpDropOpen(true)}
                    onBlur={() => setTimeout(() => setEmpDropOpen(false), 150)}
                    placeholder="Search employee by name or code…"
                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-blue-300 bg-white focus:border-blue-500 outline-none text-sm"
                  />
                  {empDropOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {matches.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-400">No active employees found</div>
                      ) : matches.map(emp => (
                        <button
                          key={emp.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pick(emp)}
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-slate-50 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800">{emp.name}</span>
                            {linkedCodes.has(emp.employee_code) && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">has login</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {emp.employee_code}{emp.designation ? ` · ${emp.designation}` : ''}{emp.department ? ` · ${emp.department}` : ''}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Username *</label>
            <input
              type="text"
              value={userFormData.username}
              onChange={(e) => setUserFormData(p => ({ ...p, username: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
              placeholder="e.g., john.doe"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
            <input
              type="text"
              value={userFormData.full_name}
              onChange={(e) => setUserFormData(p => ({ ...p, full_name: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
              placeholder="e.g., John Doe"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Employee Code</label>
          <input
            type="text"
            value={userFormData.employee_code}
            onChange={(e) => setUserFormData(p => ({ ...p, employee_code: e.target.value }))}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
            placeholder="e.g., EMP-001"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
          <input
            type="email"
            value={userFormData.email}
            onChange={(e) => setUserFormData(p => ({ ...p, email: e.target.value }))}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
            placeholder="e.g., john@example.com"
          />
        </div>

        {!selectedUser && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Password *</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={userFormData.password}
                onChange={(e) => setUserFormData(p => ({ ...p, password: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
                placeholder="Minimum 6 characters"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        )}

        {!selectedUser && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm Password *</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={userFormData.confirmPassword}
              onChange={(e) => setUserFormData(p => ({ ...p, confirmPassword: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
              placeholder="Confirm password"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Assign Role</label>
          {/* role_id stored as string so <select> value matching works correctly */}
          <select
            value={userFormData.role_id}
            onChange={(e) => setUserFormData(p => ({ ...p, role_id: e.target.value }))}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
          >
            <option value="">-- Select a role --</option>
            {roles.map(role => (
              <option key={String(role.id)} value={String(role.id)}>{role.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">SW Crew App Modules</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CREW_MODULES.map(([key, title, hint]) => (
              <label key={key} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${userFormData[key] ? 'border-green-400 bg-green-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input
                  type="checkbox"
                  checked={!!userFormData[key]}
                  onChange={(e) => setUserFormData(p => ({ ...p, [key]: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 rounded"
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-800">{title}</span>
                  <span className="block text-xs text-slate-500">{hint}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">The user must be linked to an employee to log in to the app. Changes apply at the next app login.</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
          <select
            value={userFormData.status}
            onChange={(e) => setUserFormData(p => ({ ...p, status: e.target.value }))}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-green-400 outline-none text-sm"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <button
          onClick={() => setUserView('list')}
          className="px-6 py-2 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveUser}
          disabled={loading}
          className="px-6 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
        >
          <Save size={16} /> {loading ? 'Saving...' : 'Save User'}
        </button>
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════
  // RENDER: RESET PASSWORD MODAL
  // ═════════════════════════════════════════════════════════════════════
  const renderResetPasswordModal = () => {
    if (!showResetPasswordModal) return null;

    const user = users.find(u => u.id === resetPasswordData.userId);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Key size={20} className="text-orange-500" />
              Reset Password
            </h3>
            <button
              onClick={() => setShowResetPasswordModal(false)}
              className="p-1 hover:bg-slate-100 rounded"
            >
              <X size={20} />
            </button>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-600">
              Resetting password for:
            </p>
            <p className="font-bold text-slate-900 mt-1">
              {user?.full_name} (@{user?.username})
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">New Password *</label>
            <input
              type="password"
              value={resetPasswordData.newPassword}
              onChange={(e) => setResetPasswordData(p => ({ ...p, newPassword: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-orange-400 outline-none text-sm"
              placeholder="Minimum 6 characters"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm New Password *</label>
            <input
              type="password"
              value={resetPasswordData.confirmPassword}
              onChange={(e) => setResetPasswordData(p => ({ ...p, confirmPassword: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:border-orange-400 outline-none text-sm"
              placeholder="Re-enter new password"
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => setShowResetPasswordModal(false)}
              className="px-4 py-2 border border-slate-300 rounded-lg font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              <Key size={16} /> {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═════════════════════════════════════════════════════════════════════
  return (
    <div className="w-full bg-gradient-to-b from-slate-50 to-white min-h-screen">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-6 py-8">
        <h1 className="text-xl font-bold">User Management</h1>
        <p className="text-slate-300 mt-2">Manage user roles, permissions, and user accounts</p>
      </div>

      {notification && (
        <div className={`mx-6 mt-4 px-4 py-3 rounded-lg flex items-center gap-2 text-sm font-semibold ${
          notification.type === 'success'
            ? 'bg-green-100 text-green-800 border border-green-300'
            : 'bg-red-100 text-red-800 border border-red-300'
        }`}>
          <AlertCircle size={16} />
          {notification.msg}
        </div>
      )}

      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex gap-8">
          <button
            onClick={() => { setActiveTab('roles'); setRoleView('list'); }}
            className={`py-4 font-semibold border-b-2 transition-colors ${
              activeTab === 'roles' ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            User Roles
            {roles.length > 0 && <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{roles.length}</span>}
          </button>
          <button
            onClick={() => { setActiveTab('accounts'); setUserView('list'); }}
            className={`py-4 font-semibold border-b-2 transition-colors ${
              activeTab === 'accounts' ? 'border-green-500 text-green-600' : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            User Accounts
            {users.length > 0 && <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{users.length}</span>}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'roles'
          ? (roleView === 'list' ? renderRolesList() : renderRoleForm())
          : (userView === 'list' ? renderUsersTab() : renderUserForm())
        }
      </div>

      {renderResetPasswordModal()}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2">{confirmDialog.title}</h3>
            <p className="text-sm text-slate-600 mb-6">{confirmDialog.message}</p>
            <div className="flex gap-3 justify-end flex-wrap">
              {confirmDialog.buttons.map((btn) => (
                <button
                  key={btn.value}
                  onClick={() => handleConfirmChoice(btn.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    btn.style === 'danger'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : btn.style === 'warning'
                      ? 'bg-orange-500 hover:bg-orange-600 text-white'
                      : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                  }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
